// src/pages/shells.ts —— D8-8 搭壳页：后端未就绪页面的原型列结构 + notReady 数据源；openUser360 委派 users.open360（C-04）
// 全部搭壳页已接通（tgbot 由 pages/tgbot 的 PAGES_TGBOT 注册，SP9）：SHELL_PAGES 为空，文件与导出名保留（契约锁锁定 PAGES_SHELLS / openUser360）
import type { PendingRouteKey } from '../../../shared/src/api/routes.pending';
import type { I18nPack } from '../../../shared/src/dc';
import { str, strArr, type AdminCtx } from '../domains/types';
import type { Page } from '../state';
import { defineView, type ColDef, type ErasedView, type Need, type PageDef, type PageMap } from './types';
import { open360 } from './users';

interface ShellView {
  key: string;
  pending: PendingRouteKey;
  cols: readonly string[]; // t 键（原型列键或 06 新增键），列表规格按 06 对应节
  primary?: { label: string; icon: '+ ' | '↻ '; need: Need };
  stats?: readonly ShellStat[];
}

// 原型统计卡（renderVals L689 / L701 / L727 / L788）：标签 / 副文本为 t 键或 `~` 前缀字面；值无数据源一律「—」（D8-8）
interface ShellStat {
  label: string;
  sub: string;
  dark?: boolean;
}

interface ShellPage {
  page: Page;
  i18n: I18nPack; // 本页所属文案包（经 PageDef.i18n 并入 table 域字典）
  rewrites: readonly string[];
  tabs: { key: string } | { labels: (t: Readonly<Record<string, unknown>>) => readonly string[] };
  views: readonly ShellView[];
}

const EMPTY = '—';

function textOf(t: Readonly<Record<string, unknown>>, k: string): string {
  return k.startsWith('~') ? k.slice(1) : str(t, k);
}

// D8-8：搭壳视图 = 原型列结构 + notReady 数据源（空态 + console.warn 每会话一次，TD-813）；主操作只 toast featurePending
function shellView(v: ShellView): ErasedView {
  const stats = v.stats;
  const route: string = v.pending; // PENDING 清空后 PendingRouteKey 推导为 never（无搭壳页），以 string 取回显段
  const echo = route.slice(route.indexOf(' ') + 1).replace(/^\/api/, '');
  const cols: readonly ColDef[] = v.cols.map((label) => ({ label }));
  return defineView<never>({
    key: v.key,
    source: { pending: v.pending },
    echo,
    cols,
    cells: () => [],
    csvName: v.key,
    ...(stats ? { stats: (ctx: AdminCtx) => stats.map((x) => ({ label: textOf(ctx.t, x.label), value: EMPTY, sub: textOf(ctx.t, x.sub), dark: !!x.dark })) } : {}),
    ...(v.primary ? { primary: { ...v.primary, run: (ctx: AdminCtx) => ctx.gp.ui.toast(str(ctx.t, 'featurePending')) } } : {}),
  });
}

const edit = (module: Need['module']): Need => ({ module, bit: 'edit' });

// 每页参数表：列键取原型 renderVals 对应页 cols（L686–L840）；pending 键序号见 docs/plans/sp7-admin-complete.md（TD-848）
// 数据依赖的副文本（原型拼 mock 计数，如「4 代付单号」）无数据源时显示「—」
// ---- W-A users·wallets ----
// ---- W-C1 payout·orders ----
// ---- W-C2 cms·admins ----
// ---- 常量结束 ----

export const SHELL_PAGES: readonly ShellPage[] = [
  // ---- W-A users·wallets ----
  // ---- W-C1 payout·orders ----
  // ---- W-B logs·audit（alerts 见 pages/ops.ts）----
  // ---- W-C2 cms·admins ----
  // ---- 条目结束 ----
];

function pageOf(p: ShellPage): PageDef {
  const tabs = p.tabs;
  return {
    page: p.page,
    i18n: p.i18n,
    rewrites: p.rewrites,
    tabLabels: 'key' in tabs ? (t) => strArr(t, tabs.key) : tabs.labels,
    views: p.views.map(shellView),
  };
}

export const PAGES_SHELLS: PageMap = Object.fromEntries(SHELL_PAGES.map((p) => [p.page, pageOf(p)]));

// 跨页下钻出口：签名受契约锁约束（TD-851），函数体为单行委派点（实现见 pages/users，06 C-04）
export function openUser360(ctx: AdminCtx, userId: number): void {
  open360(ctx, userId);
}
